import React from 'react';
import Dashboard from "views/Dashboard/Dashboard";
import DashboardGlobal from "views/Dashboard/DashboardGlobal";
import Event from "views/Dashboard/Event";
import Profile from "views/Dashboard/Profile";
import Billing from "views/Dashboard/Billing";
import SignIn from "views/Auth/SignIn.js";
import SignUp from "views/Auth/SignUp.js";
import EditOrder from "views/Dashboard/EditOrder";
import Suscriptions from "views/Dashboard/Suscriptions";
import CheckoutCustomOrder from "views/Dashboard/Suscriptions/components/CheckoutCustomOrder";
import CheckoutCustomOrderProduct from "views/Dashboard/Suscriptions/components/CheckoutCustomOrderProduct";
import PointOfSale from "views/Dashboard/Suscriptions/components/PointOfSale";
import Events from "views/Dashboard/Events";
import Agenda from "views/Dashboard/Agenda";
import AtencionView from "views/Dashboard/Atencion";
import EvaluacionesDiferidas from "views/Dashboard/EvaluacionesDiferidas";
import SistemaUsuarios from "views/Dashboard/Sistema";
import Scanner from "views/Dashboard/Scanner";
import ScannerUsb from "views/Dashboard/ScannerUsb";
import ScannerManual from "views/Dashboard/ScannerManual";
import AccessControl from "views/Dashboard/AccessControl";
import AppointmentBooking from "views/Public/AppointmentBooking";
import { useModuleCheck, useRoleModuleAccess, useCanOpenScanner, useUserAuth, MODULES } from 'utils/site';
import {
  HomeIcon,
  CreditIcon,
  PersonIcon,
  DocumentIcon,
  RocketIcon,
  CalendarIcon,
  SettingsIcon,
  ClockIcon,
} from "components/Icons/Icons";

// Hook personalizado para obtener rutas filtradas por roles
export const useFilteredRoutes = () => {
  // Verificar acceso a módulos por rol
  const hasPaymentsAccess = useRoleModuleAccess(MODULES.PAYMENTS);
  const hasTicketsAccess = useRoleModuleAccess(MODULES.TICKETS);
  const hasAgendaAccess = useRoleModuleAccess(MODULES.AGENDA);
  const hasSubscriptionsAccess = useRoleModuleAccess(MODULES.SUBSCRIPTIONS);
  const hasSistemaAccess = useRoleModuleAccess(MODULES.SISTEMA);
  const hasSurveysAccess = useRoleModuleAccess(MODULES.SURVEYS);
  const hasAccessControlAccess = useRoleModuleAccess(MODULES.ACCESS_CONTROL);
  const canOpenScanner = useCanOpenScanner();
  const { user } = useUserAuth();
  // "Atención" queda solo para administrador (a diferencia de "Eval. Pendientes",
  // que sigue bajo hasSurveysAccess sin cambios y mantiene a profesor). Igual que el
  // guard en AtencionView, es lista blanca por administrador en vez de bloquear
  // profesor/teacher puntual, para cubrir también inspector y roles personalizados.
  const hasAtencionAccess = hasSurveysAccess && user?.role === 'administrador';

  // Debug logging para ver los valores

  // Array base de rutas con filtros condicionales
  return [
    {
      path: "/dashboard",
      name: "Dashboard", 
      rtlName: "لوحة القيادة",
      icon: <HomeIcon color="inherit" />,
      component: DashboardGlobal,
      layout: "/admin",
    },
    // Usuarios - Solo administradores
    ...(hasSubscriptionsAccess ? [{
      path: "/usuarios",
      name: "Alumnos",
      rtlName: "لوحة القيادة",
      icon: <PersonIcon color="inherit" />,
      component: Suscriptions,
      layout: "/admin",
    }] : []),
    // Pagos - Solo administradores
    ...(hasPaymentsAccess ? [{
      path: "/billing",
      name: "Pagos",
      rtlName: "الفوترة", 
      icon: <CreditIcon color="inherit" />,
      component: Billing,
      layout: "/admin",
    }] : []),
    // Eventos - Solo administradores
    ...(hasTicketsAccess ? [
      {
        path: "/eventos",
        name: "Eventos",
        rtlName: "لوحة القيادة",
        icon: <CreditIcon color="inherit" />,
        component: Events,
        layout: "/admin",
      },
      {
        path: "/evento/:id", 
        name: "Evento",
        rtlName: "لوحة القيادة",
        icon: <CreditIcon color="inherit" />,
        secondaryNavbar: true,
        component: Event,
        layout: "/admin",
        redirect: true,
      }
    ] : []),
    // Agenda - Profesores y administradores
    ...(hasAgendaAccess ? [
      {
        path: "/agenda",
        name: "Agenda",
        rtlName: "جدول الأعمال",
        icon: <CalendarIcon color="inherit" />,
        component: Agenda,
        layout: "/admin",
      }
    ] : []),
    // Atención - Solo administradores (profesor no tiene acceso a esta sección)
    ...(hasAtencionAccess ? [
      {
        path: "/atencion",
        name: "Atención",
        rtlName: "الرعاية",
        icon: <DocumentIcon color="inherit" />,
        component: AtencionView,
        layout: "/admin",
      }
    ] : []),
    // Eval. Pendientes - Administradores y profesores
    ...(hasSurveysAccess ? [
      {
        path: "/evaluaciones-diferidas",
        name: "Eval. Pendientes",
        rtlName: "التقييمات المؤجلة",
        icon: <DocumentIcon color="inherit" />,
        component: EvaluacionesDiferidas,
        layout: "/admin",
      }
    ] : []),
    // Sistema - Solo administradores
    ...(hasSistemaAccess ? [
      {
        path: "/sistema",
        name: "Sistema",
        rtlName: "النظام",
        icon: <SettingsIcon color="inherit" />,
        component: SistemaUsuarios,
        layout: "/admin",
      }
    ] : []),
    // Scanner QR - Solo administrador e inspector (useCanOpenScanner, no
    // SUBSCRIPTIONS: ese flag también da acceso a profesor/teacher).
    ...(canOpenScanner ? [
      {
        path: "/scanner",
        name: "Escáner QR",
        rtlName: "مسح QR",
        icon: <RocketIcon color="inherit" />,
        component: Scanner,
        layout: "/admin",
        secondaryNavbar: true,
        redirect: true,
      },
      {
        path: "/scanner-usb",
        name: "Lector USB",
        rtlName: "قارئ USB",
        icon: <RocketIcon color="inherit" />,
        component: ScannerUsb,
        layout: "/admin",
        secondaryNavbar: true,
        redirect: true,
      },
      {
        path: "/scanner-manual",
        name: "Escáner Manual",
        rtlName: "الماسح اليدوي",
        icon: <RocketIcon color="inherit" />,
        component: ScannerManual,
        layout: "/admin",
        secondaryNavbar: true,
        redirect: true,
      },
    ] : []),
    // Accesos QR (lectores, historial, estados y salidas) - Administradores y profesores
    ...(hasAccessControlAccess ? [
      {
        path: "/accesos-qr",
        name: "Accesos QR",
        rtlName: "الدخول QR",
        icon: <ClockIcon color="inherit" />,
        component: AccessControl,
        layout: "/admin",
      },
    ] : []),
    // Perfil - Siempre disponible (todos los roles pueden ver perfiles)
    {
      path: "/usuario/:id",
      name: "Perfil",
      rtlName: "لوحة القيادة",
      icon: <PersonIcon color="inherit" />,
      secondaryNavbar: true,
      component: Profile,
      layout: "/admin",
      redirect: true,
    },
    // Importar - Solo administradores
    ...(hasSubscriptionsAccess ? [{
      path: "/import",
      name: "Importar",
      rtlName: "لوحة القيادة",
      icon: <RocketIcon color="inherit" />,
      secondaryNavbar: true,
      component: EditOrder,
      layout: "/admin",
    }] : []),
    // Cliente - Rutas públicas siempre disponibles
    {
      name: "Cliente",
      category: "account", 
      rtlName: "صفحات",
      state: "pageCollapse",
      redirect: true,
      views: [
      {
        path: "/signin",
        name: "Iniciar Sesión",
        rtlName: "لوحة القيادة",
        icon: <DocumentIcon color="inherit" />,
        component: SignIn,
        layout: "/auth",
      },
      {
        path: "/signup",
        name: "Registrarse",
        rtlName: "لوحة القيادة",
        icon: <RocketIcon color="inherit" />,
        secondaryNavbar: true,
        component: SignUp,
        layout: "/auth",
      },
      {
        path: "/checkout",
        name: "Inicializar Pago",
        rtlName: "لوحة القيادة",
        icon: <DocumentIcon color="inherit" />,
        secondaryNavbar: true,
        component: CheckoutCustomOrder,
        layout: "/checkout",
      },
      {
        path: "/:siteId/:productId",
        name: "Plan de Suscripción",
        rtlName: "لوحة القيادة",
        icon: <PersonIcon color="inherit" />,
        secondaryNavbar: true,
        component: CheckoutCustomOrderProduct,
        layout: "/checkout",
        redirect: true,
      },
      {
        path: "/:siteId",
        name: "Plan de Suscripción",
        rtlName: "لوحة القيادة",
        icon: <PersonIcon color="inherit" />,
        secondaryNavbar: true,
        component: PointOfSale,
        layout: "/checkout",
        redirect: true,
      },
      {
        path: "/reservar-hora/:teacherId",
        name: "Reservar Hora",
        rtlName: "حجز موعد",
        icon: <CalendarIcon color="inherit" />,
        component: AppointmentBooking,
        layout: "/public",
        public: true,
      },
      ],
    },
  ];
};

// Array de rutas por defecto (para compatibilidad con componentes que no usan hooks)
const dashRoutes = [
  {
    path: "/dashboard",
    name: "Dashboard",
    rtlName: "لوحة القيادة", 
    icon: <HomeIcon color="inherit" />,
    component: DashboardGlobal,
    layout: "/admin",
  },
  {
    path: "/billing",
    name: "Pagos",
    rtlName: "الفوترة",
    icon: <CreditIcon color="inherit" />,
    component: Billing,
    layout: "/admin",
  },
  ...(useModuleCheck(MODULES.TICKETS) ? [
    {
      path: "/eventos",
      name: "Eventos",
      rtlName: "لوحة القيادة",
      icon: <CreditIcon color="inherit" />,
      component: Events,
      layout: "/admin",
    },
    {
      path: "/evento/:id",
      name: "Evento", 
      rtlName: "لوحة القيادة",
      icon: <CreditIcon color="inherit" />,
      secondaryNavbar: true,
      component: Event,
      layout: "/admin",
      redirect: true,
    }
  ] : []),
  ...(useModuleCheck(MODULES.AGENDA) ? [
    {
      path: "/agenda",
      name: "Agenda",
      rtlName: "جدول الأعمال",
      icon: <CalendarIcon color="inherit" />,
      component: Agenda,
      layout: "/admin",
    }
  ] : []),
  {
    path: "/sistema",
    name: "Sistema",
    rtlName: "النظام",
    icon: <SettingsIcon color="inherit" />,
    component: SistemaUsuarios,
    layout: "/admin",
    redirect: true,
  },
  {
    path: "/usuarios",
    name: "Usuarios",
    rtlName: "لوحة القيادة",
    icon: <PersonIcon color="inherit" />,
    component: Suscriptions,
    layout: "/admin",
  },
  {
    path: "/usuario/:id",
    name: "Perfil",
    rtlName: "لوحة القيادة",
    icon: <PersonIcon color="inherit" />,
    secondaryNavbar: true,
    component: Profile,
    layout: "/admin",
    redirect: true,
  },
  {
    path: "/import",
    name: "Importar",
    rtlName: "لوحة القيادة",
    icon: <RocketIcon color="inherit" />,
    secondaryNavbar: true,
    component: EditOrder,
    layout: "/admin",
  },
  {
    path: "/scanner",
    name: "Escáner QR",
    rtlName: "مسح QR",
    icon: <RocketIcon color="inherit" />,
    component: Scanner,
    layout: "/admin",
    secondaryNavbar: true,
    redirect: true,
  },
  {
    path: "/scanner-usb",
    name: "Lector USB",
    rtlName: "قارئ USB",
    icon: <RocketIcon color="inherit" />,
    component: ScannerUsb,
    layout: "/admin",
    secondaryNavbar: true,
    redirect: true,
  },
  {
    path: "/scanner-manual",
    name: "Escáner Manual",
    rtlName: "الماسح اليدوي",
    icon: <RocketIcon color="inherit" />,
    component: ScannerManual,
    layout: "/admin",
    secondaryNavbar: true,
    redirect: true,
  },
  {
    path: "/accesos-qr",
    name: "Accesos QR",
    rtlName: "الدخول QR",
    icon: <ClockIcon color="inherit" />,
    component: AccessControl,
    layout: "/admin",
  },
  {
    path: "/atencion",
    name: "Atención",
    rtlName: "الرعاية",
    icon: <DocumentIcon color="inherit" />,
    component: AtencionView,
    layout: "/admin",
  },
  {
    path: "/evaluaciones-diferidas",
    name: "Eval. Pendientes",
    rtlName: "التقييمات المؤجلة",
    icon: <DocumentIcon color="inherit" />,
    component: EvaluacionesDiferidas,
    layout: "/admin",
  },
  {
    name: "Cliente",
    category: "account",
    rtlName: "صفحات",
    state: "pageCollapse",
    redirect: true,
    views: [
      {
        path: "/signin",
        name: "Iniciar Sesión",
        rtlName: "لوحة القيادة",
        icon: <DocumentIcon color="inherit" />,
        component: SignIn,
        layout: "/auth",
      },
      {
        path: "/signup",
        name: "Registrarse",
        rtlName: "لوحة القيادة",
        icon: <RocketIcon color="inherit" />,
        secondaryNavbar: true,
        component: SignUp,
        layout: "/auth",
      },
      {
        path: "/checkout",
        name: "Inicializar Pago",
        rtlName: "لوحة القيادة",
        icon: <DocumentIcon color="inherit" />,
        secondaryNavbar: true,
        component: CheckoutCustomOrder,
        layout: "/checkout",
      },
      {
        path: "/:siteId/:productId",
        name: "Plan de Suscripción",
        rtlName: "لوحة القيادة",
        icon: <PersonIcon color="inherit" />,
        secondaryNavbar: true,
        component: CheckoutCustomOrderProduct,
        layout: "/checkout",
        redirect: true,
      },
      {
        path: "/:siteId",
        name: "Plan de Suscripción", 
        rtlName: "لوحة القيادة",
        icon: <PersonIcon color="inherit" />,
        secondaryNavbar: true,
        component: PointOfSale,
        layout: "/checkout",
        redirect: true,
      },
      {
        path: "/reservar-hora/:teacherId",
        name: "Reservar Hora",
        rtlName: "حجز موعد",
        icon: <CalendarIcon color="inherit" />,
        component: AppointmentBooking,
        layout: "/public",
        public: true,
      },
    ],
  },
];

export default dashRoutes;